import {
  GearsConfigurationProvider,
  GearsConfigurationProviderProps,
} from "@made-people/gears-configuration";
import { globalStoryblokStoriesContext } from "./utils/with-global-story";
import {
  TranslationsProvider,
  TranslationsProviderProps,
  createTranslateFunction,
  TranslateFunction,
} from "@made-people/grease-translations";
import { ImageProcessorProvider } from "@made-people/grease-image";
import {
  LinkProvider,
  LinkProviderProps,
  WhitelistedQueryParamsProvider,
  WhitelistedQueryParamsProviderProps,
} from "@made-people/grease-link";
import { gearsLoader } from "@made-people/gears-remix";
import {
  StoryblokComponentProvider,
  StoryblokComponentProviderProps,
  isStoryblokPreviewRequestQuery,
  StoryblokPreviewLink,
} from "@made-people/grease-storyblok";
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useRouteLoaderData,
  useSearchParams,
} from "@remix-run/react";
import {
  CentraSearchProvider,
  CentraSearchProviderProps,
  CentraProvider,
  CentraProviderProps,
  CentraAutoCompleteProvider,
  centraCheckoutApiCustomerToGreaseCustomer,
  CentraAccountClient,
  CentraAccountMappers,
  makeCentraCheckoutApiOrderToGreaseOrderMapper,
} from "@made-people/grease-centra";
import {
  CentraGetProductsPayload,
  createCentraCheckoutAPIClient,
  CentraLocalStorageTokenHandler,
} from "@made-people/centra-checkout-api";
import { bffCentraSearch } from "./services/bff/bff-centra-search";
import { bffInitializeSelection } from "./services/bff/bff-initialize-selection";
import { useFrontendContext } from "./hooks/use-frontend-context";
import {
  NewsletterProvider,
  NewsletterProviderProps,
} from "@made-people/grease-newsletter";
import { GtmProvider, GtmProviderProps } from "@made-people/grease-gtm";
import {
  GlobalProductSettingsProvider,
  ProductOptionSortOrder,
} from "@made-people/grease-product";
import {
  SearchProvider,
  SearchProviderProps,
} from "@made-people/grease-search";
import { CountryCode } from "@made-people/grease-countries-and-languages";
import { SbBlokData } from "@storyblok/js";
import { MissMaryLink } from "./components/global/miss-mary-link";
import { bffCheckoutApiGetProducts } from "./services/bff/bff-checkout-api-get-products";
import { getCentraCheckoutApiMapperOptions } from "./services/centra/mappers/utils/get-centra-mapper-options";
import { mmCentraMappers } from "./services/centra/mappers/mm-centra-mappers";
import {
  AccountProvider,
  AccountProviderProps,
} from "@made-people/grease-account";
import {
  AlgoliaBootstrap,
  AlgoliaBootstrapProps,
} from "./services/algolia/algolia-bootstrap";
import {
  MmEmarsysConfig,
  MmEmarsysNewsletterClient,
} from "./services/emarsys/mm-emarsys-newsletter-client";
import { MmEmarsysConfigProvider } from "./services/emarsys/mm-emarsys-config-provider";
import { MMNewsletterOrderListener } from "./components/checkout/MMNewsletterOrderListener";
import { useMmBackendClient } from "./hooks/use-mm-backend-client";
import { getLoaderCustomExtraDataOrNull } from "./utils/loader-custom-extra-data";
import { mmRootLoader } from "./root-loader/mm-root-loader";
import { createStoryblokTransformers } from "./bootstrap/storyblok/create-storyblok-transformers";
import { imageProcessorProps } from "./bootstrap/image-processor";
import { getSizechartFacetExclusions } from "./bootstrap/sizechart-facet-exclusion-mapper";
import { createMissMaryFacetGroupingFunction } from "./bootstrap/miss-mary-facet-groups";
import {
  MmCountriesAndLanguagesProvider,
  MmCountriesAndLanguagesProviderProps,
} from "./components/global/mm-countries-and-languages-provider";
import {
  greaseProductToUnformattedStructuredDataProduct,
  StructuredDataProvider,
} from "@made-people/grease-structured-data";
import type { StructuredDataProviderProps } from "@made-people/grease-structured-data";
import { mmCentraProductToStructuredDataProduct } from "./services/centra/mappers/mm-centra-product-to-structured-data-product";
import { useGetMerchantReturnPolicySettings } from "./hooks/use-merchant-return-policy-settings";
import { useGetOfferShippingDetailsSettings } from "./hooks/use-offer-shipping-details-settings";
import {
  TrustpilotProvider,
  TrustpilotProviderProps,
} from "@made-people/grease-trustpilot";
import { useBffClient } from "./hooks/use-bff-client";
import { fetchTrustpilotStoreReviews } from "./services/trustpilot/fetch-trustpilot-store-reviews";
import { MmAllocationRuleProvider } from "./services/centra/allocation-rule/mm-allocation-rule-provider";
import { useAllocationRuleFetch } from "./services/centra/allocation-rule/use-allocation-rule";
import { useMemo } from "react";
import {
  trustpilotReviewsToSchemaFields,
  useMmPdpReviewsForSchema,
  withMmBrandFallback,
} from "./services/structured-data/mm-pdp-structured-data";

const optionSortOrder: ProductOptionSortOrder = {
  "waist-size": "numeric",
  "cup-size": "alphabetical",
};

export interface BootstrapProps {
  children: React.ReactNode;
  rootLoaderData: ReturnType<typeof useLoaderData<typeof mmRootLoader>>;
}

/**
 * A wrapper for all grease things to keep root.tsx clean
 */
export const _Bootstrap = ({ children, rootLoaderData }: BootstrapProps) => {
  const offerShippingDetailsSettings = useGetOfferShippingDetailsSettings();
  const merchantReturnPolicySettings = useGetMerchantReturnPolicySettings();

  // Retrieve values used to configure the providers
  const location = useLocation();
  const navigate = useNavigate();

  const wildcardPageLoaderData = useRouteLoaderData<typeof gearsLoader>(
    "project-router-wildcard",
  );
  const blankPageLoaderData =
    useRouteLoaderData<typeof gearsLoader>("blank-page");

  const pageLoaderData = wildcardPageLoaderData ?? blankPageLoaderData;

  const bffClient = useBffClient();
  const frontendContext = useFrontendContext();
  const mmBackendClient = useMmBackendClient();
  const { allocationRule, isLoading: isAllocationRuleLoading } =
    useAllocationRuleFetch(bffClient, frontendContext?.market ?? "");
  const [searchParams] = useSearchParams();

  const pageLoaderPage = pageLoaderData?.pageResponse?.page;
  const isCentraProductPage = pageLoaderPage?.pageType === "centraProductPage";
  const pdpProductData =
    isCentraProductPage && pageLoaderPage && "productData" in pageLoaderPage
      ? pageLoaderPage.productData
      : undefined;
  const pdpReviewsForSchema = useMmPdpReviewsForSchema(
    bffClient,
    pdpProductData,
    frontendContext?.language ?? "",
    isCentraProductPage,
  );
  const pdpProduct = useMemo(() => {
    if (!isCentraProductPage) {
      return undefined;
    }
    const base = withMmBrandFallback(
      mmCentraProductToStructuredDataProduct(
        pdpProductData,
        frontendContext?.country ?? "",
      ),
    );
    if (!base) {
      return undefined;
    }
    return {
      ...base,
      ...trustpilotReviewsToSchemaFields(pdpReviewsForSchema),
    };
  }, [isCentraProductPage, pdpProductData, pdpReviewsForSchema]);
  // All hook calls (use*) must appear above this comment — the early
  // `return null` guards below mean hooks called after them would run
  // conditionally and break Rules of Hooks.

  // Used for reset password in centra
  const id = searchParams.get("id");
  const i = searchParams.get("i");

  if (!frontendContext) {
    return null;
  }

  if (!rootLoaderData || !pageLoaderData) {
    // Find out when this can happen and handle appropriately
    return null;
  }

  // Only render content if we should
  const pageType = pageLoaderData.pageResponse.page.pageType;
  if (pageType === "rawResponse" || pageType === "redirect") {
    return null;
  }

  const customExtraData = getLoaderCustomExtraDataOrNull(
    pageLoaderData.customExtraData,
  );
  if (!customExtraData) {
    return null;
  }

  /**
   *
   * StoryblokComponentProvider configuration
   *
   */
  const isInsideStoryblokPreview = isStoryblokPreviewRequestQuery(
    new URLSearchParams(location.search),
  );
  const insideStoryblokPreview = (blok: SbBlokData) => {
    if (!isInsideStoryblokPreview) {
      return false;
    }

    if (!blok.component) {
      return true;
    }

    // Line up components to skip rendering of visual preview. Typically,
    // components that do cloneElement on children should be skipped.
    return !["CentraSearchResultsProvider", "MenuItem"].includes(
      blok.component,
    );
  };
  const storyblokComponentProviderProps: StoryblokComponentProviderProps = {
    transformers: createStoryblokTransformers(insideStoryblokPreview),
  };

  /**
   *
   * WhitelistedQueryParamsProvider configuration
   *
   */
  const whitelistedQueryParamsProviderProps: WhitelistedQueryParamsProviderProps =
    {
      urlSearchParams: new URLSearchParams(location.search),
    };

  /**
   *
   * Centra Providers
   *
   */
  const centraTokenHandler = new CentraLocalStorageTokenHandler();

  const centraCheckoutAPIClient = createCentraCheckoutAPIClient({
    baseUrl: rootLoaderData.settingsAndContentResponse.centraCheckoutApiBaseUrl,
    centraTokenHandler,
  });

  centraCheckoutAPIClient.getSelection = async () => {
    return bffInitializeSelection(
      bffClient,
      centraTokenHandler,
      frontendContext,
    );
  };

  centraCheckoutAPIClient.getProducts = async (payload) => {
    return await bffCheckoutApiGetProducts(
      payload,
      bffClient,
      centraTokenHandler,
      frontendContext,
    );
  };

  const centraProviderProps: CentraProviderProps = {
    mapperOptions: {
      options: getCentraCheckoutApiMapperOptions(
        frontendContext,
        allocationRule?.warehouses
          ? {
              name: allocationRule.name,
              hasLitWarehouse: allocationRule.warehouses.some(
                (w) => w.name === "LIT",
              ),
            }
          : undefined,
      ),
      mappers: mmCentraMappers,
    },
    checkoutAPIClient: centraCheckoutAPIClient,
    initializationType:
      // "dontInitialize",
      pageType === "checkoutFinalize" ? "dontInitialize" : "onLoad",
    queryContext: {
      market: frontendContext.market,
      pricelist: frontendContext.pricelist,
      language: frontendContext.language,
    },
  };

  const centraSearchProviderProps: CentraSearchProviderProps = {
    searchFunctionGetter: (payload: CentraGetProductsPayload) => {
      return bffCentraSearch(bffClient, payload, frontendContext);
    },
  };

  /**
   *
   * LinkProvider configuration
   *
   */
  const linkProviderNavigateFunction = (url: string) => {
    const [path, params] = url.split("?");
    navigate({
      pathname: path,
      search: params,
    });
  };

  const LinkComponent = isInsideStoryblokPreview
    ? StoryblokPreviewLink
    : MissMaryLink;
  const linkProviderProps: LinkProviderProps = {
    siteSlug:
      rootLoaderData.settingsAndContentResponse.frontendContext.siteSlug,
    LinkComponent,
    navigate: linkProviderNavigateFunction,
  };

  /**
   *
   * NewsletterProvider configuration
   *
   */
  const emarsysConfig: MmEmarsysConfig = {
    language: frontendContext.language,
    country: frontendContext.country,
    optinMethod: "newsletter",
  };

  const newsletterProviderProps: NewsletterProviderProps = {
    newsletterClientDriver: new MmEmarsysNewsletterClient(
      mmBackendClient,
      emarsysConfig,
    ),
  };

  /**
   *
   * TranslationsProvider configuration
   *
   */
  const translate: TranslateFunction = createTranslateFunction(
    rootLoaderData.settingsAndContentResponse.translations,
  );
  const translationsProviderProps: TranslationsProviderProps = {
    value: translate,
  };

  /**
   *
   * GtmProvider configuration
   *
   */
  const containerIds =
    rootLoaderData.settingsAndContentResponse.gtm?.containerIds;

  const gtmProviderProps: GtmProviderProps = {
    containerIds: containerIds ?? [],
    globalFields: {
      ecommerce: {
        currency: frontendContext.currency.currency!,
      },
    },
  };

  /**
   * SearchProvider configuration
   *
   */
  const excludedFacets = getSizechartFacetExclusions(frontendContext.country);
  const facetGrouping = createMissMaryFacetGroupingFunction(
    frontendContext.country,
  );
  const searchProviderProps: SearchProviderProps = {
    structuredDataMapper: greaseProductToUnformattedStructuredDataProduct,
    searchData: {
      urlSearchParams: searchParams,
      pathname: location.pathname,
    },
    globalSearchSettings: {
      defaultItemsPerPage: 12,
    },
    excludedFacets,
    facetGrouping,
    facetSorting: { values: "name" },
  };

  /**
   *
   * AlgoliaBootstrap configuration
   *
   */
  const algoliaProviderProps: AlgoliaBootstrapProps = {
    language: frontendContext.language,
    market: frontendContext.market,
    pricelist: frontendContext.pricelist,
    countryCode: frontendContext.country,
  };

  /**
   *
   * MmCountriesAndLanguagesProviderProps
   *
   */
  const mmCountriesAndLanguagesProviderProps: MmCountriesAndLanguagesProviderProps =
    {
      frontendContext,
      countriesAndLanguages: {
        ...rootLoaderData.settingsAndContentResponse.countriesAndLanguages,
        countries:
          rootLoaderData.settingsAndContentResponse.countriesAndLanguages.countries.map(
            (country) => ({
              ...country,
              countryCode: country.countryCode as CountryCode,
            }),
          ),
      },
      sites: rootLoaderData.settingsAndContentResponse.sites,
    };

  /**
   *
   * AccountProvider configuration
   *
   */
  const orderMapper = makeCentraCheckoutApiOrderToGreaseOrderMapper(
    centraProviderProps.mapperOptions.options,
    centraProviderProps.mapperOptions.mappers,
  );
  const mappers: CentraAccountMappers = {
    orderMapper,
    customerMapper: centraCheckoutApiCustomerToGreaseCustomer,
  };
  const accountProviderProps: AccountProviderProps = {
    // Don't initialize until we have an account implementation
    initializeOnMount: true,

    accountClientDriver: new CentraAccountClient(
      centraCheckoutAPIClient,
      mappers,
      {
        resetPasswordUri: "comfort-club/reset-password",
        resetPasswordData: { id, i },
      },
    ),
  };

  /**
   *
   * StructuredDataProvider configuration
   *
   */
  const structuredDataProviderProps: StructuredDataProviderProps = {
    mappers: {
      greaseProductToStructuredDataProduct:
        greaseProductToUnformattedStructuredDataProduct,
    },
    context: {
      country: frontendContext.country,
      currencyCode: frontendContext.currencyCode,
      siteSlug: frontendContext.siteSlug,
      baseUrl: frontendContext.baseUrl,
      currentPath: location.pathname,
    },
    pdpProduct,
    breadCrumbs: pageLoaderData.pageResponse.page.breadCrumbs,
    offerShippingDetailsSettings,
    merchantReturnPolicySettings,
  };
  /**
   * TrustpilotProvider configuration
   *
   */
  const storeReviewsFetchOptions = {
    language: frontendContext.language,
    perPage: 12,
    page: 1,
    stars: [5],
  };

  const trustpilotProviderProps: TrustpilotProviderProps = {
    storeReviews: () =>
      fetchTrustpilotStoreReviews(bffClient, storeReviewsFetchOptions),
    siteLanguage: `${frontendContext.language}-${frontendContext.country}`,
    bffClient,
    storeReviewsFetchOptions,
  };

  return (
    <StoryblokComponentProvider {...storyblokComponentProviderProps}>
      <globalStoryblokStoriesContext.Provider
        value={
          rootLoaderData?.settingsAndContentResponse.globalStoryblokStories ??
          []
        }
      >
        <WhitelistedQueryParamsProvider
          {...whitelistedQueryParamsProviderProps}
        >
          <ImageProcessorProvider {...imageProcessorProps}>
            <StructuredDataProvider {...structuredDataProviderProps}>
              <LinkProvider {...linkProviderProps}>
                <GtmProvider {...gtmProviderProps}>
                  <TrustpilotProvider {...trustpilotProviderProps}>
                    <SearchProvider {...searchProviderProps}>
                      <AlgoliaBootstrap {...algoliaProviderProps}>
                        <MmEmarsysConfigProvider {...emarsysConfig}>
                          <NewsletterProvider {...newsletterProviderProps}>
                            <TranslationsProvider
                              {...translationsProviderProps}
                            >
                              <GlobalProductSettingsProvider
                                strictVariantMatching={true}
                                optionSortOrder={optionSortOrder}
                              >
                                <CentraProvider {...centraProviderProps}>
                                  <MmAllocationRuleProvider
                                    allocationRule={allocationRule}
                                    isLoading={isAllocationRuleLoading}
                                  >
                                    <CentraAutoCompleteProvider>
                                      <CentraSearchProvider
                                        {...centraSearchProviderProps}
                                      >
                                        <MmCountriesAndLanguagesProvider
                                          {...mmCountriesAndLanguagesProviderProps}
                                        >
                                          <AccountProvider
                                            {...accountProviderProps}
                                          >
                                            <MMNewsletterOrderListener />
                                            <html
                                              lang={
                                                pageLoaderData.pageResponse.page
                                                  .meta.canonicalHreflangLocale
                                                  .hreflang
                                              }
                                            >
                                              {children}
                                            </html>
                                          </AccountProvider>
                                        </MmCountriesAndLanguagesProvider>
                                      </CentraSearchProvider>
                                    </CentraAutoCompleteProvider>
                                  </MmAllocationRuleProvider>
                                </CentraProvider>
                              </GlobalProductSettingsProvider>
                            </TranslationsProvider>
                          </NewsletterProvider>
                        </MmEmarsysConfigProvider>
                      </AlgoliaBootstrap>
                    </SearchProvider>
                  </TrustpilotProvider>
                </GtmProvider>
              </LinkProvider>
            </StructuredDataProvider>
          </ImageProcessorProvider>
        </WhitelistedQueryParamsProvider>
      </globalStoryblokStoriesContext.Provider>
    </StoryblokComponentProvider>
  );
};

/**
 * Wrapper of _Bootstrap because gears config needs to be provided
 * before it renders
 */
export const Bootstrap = (props: BootstrapProps) => {
  if (!props.rootLoaderData) {
    return null;
  }

  /**
   *
   * GearsConfigurationProvider configuration
   *
   */
  const gearsConfigurationProps: GearsConfigurationProviderProps = {
    gearsConfiguration: props.rootLoaderData.gearsConfiguration,
  };

  return (
    <GearsConfigurationProvider {...gearsConfigurationProps}>
      <_Bootstrap {...props}>{props.children}</_Bootstrap>
    </GearsConfigurationProvider>
  );
};
